import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { shopApi, useShop, INR } from '../lib/shop';
import { OFFER_LABEL } from '../lib/catalog';

export const offPct = (p) =>
  p.original_price && p.original_price > p.price ? Math.round(100 - (p.price * 100) / p.original_price) : 0;

export const isAvailable = (p) => p.in_stock !== false && (p.stock === undefined || p.stock > 0);

export const PriceRow = ({ p, size = 'sm' }) => {
  const off = offPct(p);
  const lg = size === 'lg';
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      {off > 0 && <span className={`line-through text-[var(--si-grey)] ${lg ? 'text-lg' : 'text-sm'}`}>{INR(p.original_price)}</span>}
      {off > 0 && <span className={`si-off ${lg ? 'text-lg' : 'text-sm'}`}>{off}% off</span>}
      <span className={`basis-full font-bold text-[var(--si-black)] leading-tight ${lg ? 'text-4xl' : 'text-xl'}`}>{INR(p.price)}</span>
    </div>
  );
};

export const OfferBadge = ({ offer, className = '' }) => offer ? (
  <span className={`inline-block bg-[var(--si-green)] text-white text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm ${className}`}>{OFFER_LABEL[offer]}</span>
) : null;

export const ProductCard = ({ p, compact = false }) => {
  const { wishlistSlugs, refreshWishlist } = useShop();
  const inWish = wishlistSlugs?.includes(p.slug);
  const available = isAvailable(p);
  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    try { await shopApi.toggleWishlist(p.slug); refreshWishlist(); }
    catch { window.location.href = '/login'; }
  };
  return (
    <Link to={`/shop/product/${p.slug}`} data-testid={`product-${p.slug}`}
      className={`group block bg-white rounded-md border border-transparent hover:border-[var(--si-line)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow p-2.5 ${compact ? 'w-[168px] md:w-[210px] flex-shrink-0' : ''}`}>
      <div className="relative aspect-[3/4] bg-[var(--si-bg-soft)] rounded overflow-hidden">
        <img src={p.images?.[0]} alt={p.name} loading="lazy"
          className={`w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ${available ? '' : 'opacity-50 grayscale'}`} />
        <button onClick={toggle} aria-label="Add to wishlist" data-testid={`wishlist-${p.slug}`}
          className="absolute top-2 right-2 p-2 rounded-full bg-white shadow-sm hover:scale-110 transition-transform">
          <Heart className={`w-4 h-4 ${inWish ? 'fill-[#E11D48] text-[#E11D48]' : 'text-[var(--si-grey)]'}`} />
        </button>
        {!available && (
          <span className="absolute inset-x-0 bottom-0 bg-black/75 text-white text-center text-sm font-bold py-1.5" data-testid={`out-of-stock-${p.slug}`}>OUT OF STOCK</span>
        )}
        {available && p.segment === 15999 && (
          <span className="absolute top-2 left-2 bg-[var(--si-navy)] text-[var(--si-yellow)] text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm">Limited</span>
        )}
      </div>
      <div className="mt-2.5 space-y-1">
        <p className="text-sm font-medium text-[var(--si-black)] leading-snug line-clamp-2 min-h-[2.5em] group-hover:text-[var(--si-blue)]">{p.name}</p>
        <PriceRow p={p} />
        <div className="flex flex-wrap gap-1">
          <OfferBadge offer={p.offer} />
          {p.coin_buttons && <span className="inline-block bg-[#FEF3C7] text-[#92400E] border border-[#FCD34D] text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm">Coin Buttons</span>}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
